import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '../../../components/tooltip/index';
import { DividendFrequency, DividendPayout } from '../../../hooks/dividend/dividend.types';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { getTranslation } from '../../../utils/get-translation.util';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';
import { DividendActionsMenu } from './dividend-actions-menu';
import { DividendInlineDateCell } from './dividend-inline-date-cell';
import { DividendStatusBadge } from './dividend-status-badge';
import { DividendColumn, dividendColumnOrderMap } from './dividends-table.types';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const FREQUENCY_LABELS: Record<DividendFrequency, string> = {
  [DividendFrequency.ANNUALLY]: getTranslation('ANNUAL'),
  [DividendFrequency.MONTHLY]: getTranslation('MONTHLY'),
  [DividendFrequency.QUARTERLY]: getTranslation('DIVIDEND_FREQUENCY_QUARTERLY'),
  [DividendFrequency.SEMI_ANNUALLY]: getTranslation('DIVIDEND_FREQUENCY_SEMI_ANNUAL'),
};

const formatPeriod = (
  frequency: DividendFrequency,
  year: number,
  periodIndex: null | number,
): string => {
  if (frequency === DividendFrequency.ANNUALLY) return `${year}`;
  if (frequency === DividendFrequency.MONTHLY && periodIndex !== null)
    return `${MONTH_NAMES[periodIndex - 1]} ${year}`;
  if (frequency === DividendFrequency.QUARTERLY && periodIndex !== null)
    return `Q${periodIndex} ${year}`;
  if (frequency === DividendFrequency.SEMI_ANNUALLY && periodIndex !== null)
    return `H${periodIndex} ${year}`;
  return `${year}`;
};

const SHARE_CLASS_LINES = 3;

const cellClass = 'relative overflow-hidden px-4';
const cellInnerClass = 'flex h-[72px] w-full items-center';
const labelClass = 'text-sm font-[450] text-gray-700';

type Props = {
  companyId: string;
  currencySymbol: string;
  onEdit: (payout: DividendPayout) => void;
  onView: (payout: DividendPayout) => void;
  payout: DividendPayout;
};

export const DividendTableRow: FC<Props> = ({
  companyId,
  currencySymbol,
  onEdit,
  onView,
  payout,
}) => {
  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });

  const totalPayout = payout.items.reduce((sum, item) => sum + (item.totalAmount ?? 0), 0);

  const shareClassNames = Array.from(
    new Set(payout.items.map((item) => item.shareClassId).filter(Boolean)),
  )
    .map((id) => shareClasses.find((sc) => sc.id === id)?.name)
    .filter((name): name is string => Boolean(name));

  const shareClassLabel =
    shareClassNames.length === 0
      ? '—'
      : shareClassNames.slice(0, 3).join(', ') +
        (shareClassNames.length > 3 ? ` +${shareClassNames.length - 3}` : '');

  const cellContent: Record<DividendColumn, ReactNode> = {
    [DividendColumn.PAYOUT_NAME]: payout.name,
    [DividendColumn.SHARE_CLASS]:
      shareClassNames.length === 0 ? (
        '—'
      ) : (
        // Stacked rather than run together on one truncated line, but never past three lines —
        // the row keeps its height, so the counter takes the last one when the list is longer.
        <span className="flex w-full flex-col leading-5">
          {shareClassNames
            .slice(
              0,
              shareClassNames.length > SHARE_CLASS_LINES
                ? SHARE_CLASS_LINES - 1
                : SHARE_CLASS_LINES,
            )
            .map((name) => (
              <span className="block max-w-full truncate" key={name}>
                {name}
              </span>
            ))}
          {shareClassNames.length > SHARE_CLASS_LINES && (
            <span className="text-brand-700">
              +{shareClassNames.length - (SHARE_CLASS_LINES - 1)}
            </span>
          )}
        </span>
      ),
    [DividendColumn.FREQUENCY]: FREQUENCY_LABELS[payout.frequency],
    [DividendColumn.PERIOD]: formatPeriod(payout.frequency, payout.year, payout.periodIndex),
    [DividendColumn.TOTAL_PAYOUT]:
      totalPayout > 0 ? `${currencySymbol}${formatDividendAmount(totalPayout)}` : '—',
    [DividendColumn.APPROVAL_DATE]: (
      <DividendInlineDateCell
        approvalDate={payout.approvalDate}
        field="approvalDate"
        payoutDate={payout.payoutDate}
        payoutId={payout.id}
      />
    ),
    [DividendColumn.PAYOUT_DATE]: (
      <DividendInlineDateCell
        approvalDate={payout.approvalDate}
        field="payoutDate"
        payoutDate={payout.payoutDate}
        payoutId={payout.id}
      />
    ),
    [DividendColumn.STATUS]: (
      <DividendStatusBadge
        approvalDate={payout.approvalDate}
        companyId={companyId}
        payoutDate={payout.payoutDate}
        payoutId={payout.id}
        status={payout.status}
      />
    ),
    [DividendColumn.ACTIONS]: (
      <DividendActionsMenu onEdit={onEdit} onView={onView} payout={payout} />
    ),
  };

  return (
    <tr className="hover:bg-gray-50">
      {dividendColumnOrderMap.map((col) => {
        const isName = col === DividendColumn.PAYOUT_NAME;
        const isShareClass = col === DividendColumn.SHARE_CLASS;

        return (
          <td className={cellClass} key={col}>
            <div className={twMerge(cellInnerClass, 'min-w-0')}>
              {isName || isShareClass ? (
                <Tooltip content={isName ? payout.name : shareClassLabel}>
                  <span
                    className={twMerge(
                      labelClass,
                      'block max-w-full',
                      isName && 'truncate',
                      isShareClass && 'min-w-0',
                    )}
                  >
                    {cellContent[col]}
                  </span>
                </Tooltip>
              ) : (
                <span className={labelClass}>{cellContent[col]}</span>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
